import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from '@phosphor-icons/react';

/** Current path + query for restoring list filters / page when leaving detail. */
export function buildFromPath(location) {
  if (!location?.pathname) return '/dashboard/tickets/list';
  return `${location.pathname}${location.search || ''}`;
}

export function backLabelForPath(fromPath) {
  const p = fromPath || '';
  if (p.includes('/my-tickets')) return 'Back to My Tickets';
  if (p.includes('/my-tasks')) return 'Back to My Tasks';
  if (p.includes('/my-requests')) return 'Back to My Requests';
  if (p.includes('/manager-queue')) return 'Back to Manager Queue';
  if (p.includes('/transfer')) return 'Back to Transfer';
  if (p.includes('/update-status')) return 'Back to Update Status';
  if (p.includes('/closure')) return 'Back to Closure';
  if (p.includes('/reopen')) return 'Back to Reopen';
  if (p.includes('/reports')) return 'Back to Reports';
  if (p.includes('/rca')) return 'Back to RCA';
  if (p.includes('/member-report')) return 'Back to Member Report';
  if (p.includes('/audit')) return 'Back to Audit Log';
  if (p.includes('/my-teams')) return 'Back to My Teams';
  if (p.includes('/whatsapp')) return 'Back to WhatsApp';
  if (p.includes('/workforce')) return 'Back to Work Intelligence';
  if (p.includes('/tickets/list')) return 'Back to All Tickets';
  if (p.includes('/tickets')) return 'Back to Tickets';
  return 'Back to tickets';
}

export function resolveListFrom(location, fallback = '/dashboard/tickets/list') {
  const from = location?.state?.from;
  if (typeof from === 'string' && from.startsWith('/dashboard')) return from;
  return fallback;
}

/** Link into ticket detail while remembering where the user came from. */
export function TicketDetailLink({ ticketNo, children, className, style, ...rest }) {
  const location = useLocation();
  return (
    <Link
      to={`/dashboard/tickets/list/${ticketNo}`}
      state={{ from: buildFromPath(location) }}
      className={className}
      style={style}
      {...rest}
    >
      {children}
    </Link>
  );
}

/**
 * Back control for action pages (transfer, status, …).
 * Returns to the ticket detail and restores the original list `from` path.
 */
export function TicketActionBack({ ticketNo, className = 'tk-back-btn', style }) {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.returnTo;
  const listFrom = location.state?.listFrom;
  const hasContext = Boolean(
    (typeof returnTo === 'string' && returnTo.startsWith('/dashboard'))
    || ticketNo,
  );
  if (!hasContext) return null;

  const label = ticketNo ? `Back to ${ticketNo}` : 'Back';

  const goBack = () => {
    if (typeof returnTo === 'string' && returnTo.startsWith('/dashboard')) {
      navigate(returnTo, { state: listFrom ? { from: listFrom } : undefined });
      return;
    }
    if (ticketNo) {
      navigate(`/dashboard/tickets/list/${ticketNo}`, {
        state: listFrom ? { from: listFrom } : undefined,
      });
      return;
    }
    navigate(-1);
  };

  return (
    <button
      type="button"
      onClick={goBack}
      className={className}
      style={style}
    >
      <ArrowLeft size={14} weight="bold" />
      {label}
    </button>
  );
}

/** Navigate from ticket detail to an action page without losing back context. */
export function navigateTicketAction(navigate, location, ticketNo, actionPath) {
  const url = `${actionPath}?ticket=${encodeURIComponent(ticketNo)}`;
  navigate(url, {
    state: {
      returnTo: `/dashboard/tickets/list/${ticketNo}`,
      listFrom: location.state?.from,
    },
  });
}
